import { ButtonV2, Dialog } from '@cimplico/components';
import { useState } from 'react';
import { toastNotification } from '../../components/toast/Toast';
import { useFirmContext } from '../../providers/FirmProvider';
import { useCancelEntityDeactivation } from '../../queries/billing/use-cancel-entity-deactivation';
import { useDeactivateEntity } from '../../queries/billing/use-deactivate-entity';
import { useSubscriptionV2 } from '../../queries/use-firm-subscription';
import { Entity } from '../../types/entity';
import { formatDate } from '../../utilities/formatting';

type DeactivateButtonProps = {
    entity: Entity;
};

function DeactivateButton({ entity }: DeactivateButtonProps): JSX.Element | null {
    const firm = useFirmContext();
    if (firm.subscriptionVersion === 1) {
        return null;
    }
    if (!entity.flaggedDeactivatedAt) {
        return <Deactivate entity={entity} />;
    }
    return <CancelDeactivation entity={entity} />;
}

function Deactivate({ entity }: DeactivateButtonProps): JSX.Element {
    const { data } = useSubscriptionV2();
    const { mutate, isLoading } = useDeactivateEntity();
    const [showConfirmation, setShowConfirmation] = useState(false);

    const subscription = data?.subscription;
    const formattedQuotaCountDate = formatDate(subscription?.nextQuotaRecount || new Date());

    function deactivate(): void {
        mutate(entity.id, {
            onSuccess: () => {
                setShowConfirmation(false);
            },
            onError: () => {
                toastNotification('error', 'Failed to deactivate entity', 'Please try again');
            },
        });
    }

    return (
        <>
            {showConfirmation && subscription && (
                <Dialog
                    type="error"
                    title={`Are you sure you want to deactivate ${entity.name}?`}
                    description={`On ${formattedQuotaCountDate}, this entity will be excluded from billing if it has no open workpapers. If any workpapers remain open, it will stay active.`}
                    isOpen
                    primaryAction="Deactivate"
                    secondaryAction="Cancel"
                    onClose={(): void => setShowConfirmation(false)}
                    onSecondaryActionClick={(): void => setShowConfirmation(false)}
                    onPrimaryActionClick={deactivate}
                    disablePrimaryAction={isLoading}
                    isLoading={isLoading}
                />
            )}
            <ButtonV2
                label="Deactivate"
                size="xs"
                type="secondary"
                isDisabled={!subscription}
                isLoading={isLoading}
                onClick={(): void => setShowConfirmation(true)}
            />
        </>
    );
}

function CancelDeactivation({ entity }: DeactivateButtonProps): JSX.Element {
    const { data } = useSubscriptionV2();
    const { mutate, isLoading } = useCancelEntityDeactivation();
    const [showConfirmation, setShowConfirmation] = useState(false);

    const subscription = data?.subscription;
    const formattedQuotaCountDate = formatDate(subscription?.nextQuotaRecount || new Date());

    function cancelDeactivation(): void {
        mutate(entity.id, {
            onSuccess: () => {
                setShowConfirmation(false);
            },
        });
    }

    return (
        <>
            {showConfirmation && subscription && (
                <Dialog
                    type="error"
                    title={`Cancel deactivation for ${entity.name}?`}
                    description={`${entity.name} will stay active and continue to count towards your subscription after ${formattedQuotaCountDate}, even if it has no open workpapers.`}
                    isOpen
                    primaryAction="Cancel Deactivation"
                    secondaryAction="Close"
                    onClose={(): void => setShowConfirmation(false)}
                    onSecondaryActionClick={(): void => setShowConfirmation(false)}
                    onPrimaryActionClick={cancelDeactivation}
                    disablePrimaryAction={isLoading}
                    isLoading={isLoading}
                />
            )}
            <ButtonV2
                label="Cancel Deactivation"
                size="xs"
                type="secondary"
                isDisabled={!subscription}
                onClick={(): void => setShowConfirmation(true)}
            />
        </>
    );
}
export default DeactivateButton;
